import type { PokemonSpec, RegulationRef, SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

// Sticky strip under the top bar. Shows the active regulation always; shows
// the user's team and opponent's team as small sprite rows once attached.
// Replaces the older right-rail pattern — keeps context permanently visible
// without scrolling, fits mobile (horizontal scroll, never collapses).
export function ContextStrip({
  regulation,
  myTeam,
  oppTeam,
}: {
  regulation: RegulationRef;
  myTeam?: PokemonSpec[] | null;
  oppTeam?: SpeciesId[] | null;
}) {
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--bg-0)',
        borderBottom: '1px solid var(--line-1)',
        padding: '6px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        overflowX: 'auto',
        whiteSpace: 'nowrap',
      }}
    >
      <RegulationChip regulation={regulation} />
      {myTeam && myTeam.length > 0 && (
        <TeamRow label="you" species={myTeam.map((p) => p.species as SpeciesId)} />
      )}
      {oppTeam && oppTeam.length > 0 && <TeamRow label="opp" species={oppTeam} />}
    </div>
  );
}

function RegulationChip({ regulation }: { regulation: RegulationRef }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px',
        background: 'var(--accent-bg)',
        border: '1px solid var(--accent-line)',
        borderRadius: 3,
        flexShrink: 0,
      }}
    >
      <span
        className="caps mono"
        style={{ fontSize: 9, color: 'var(--accent-2)', letterSpacing: '0.08em' }}
      >
        {regulation.label}
      </span>
    </div>
  );
}

function TeamRow({ label, species }: { label: string; species: SpeciesId[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <span
        className="caps mono"
        style={{ color: 'var(--fg-4)', fontSize: 9, letterSpacing: '0.08em', minWidth: 22 }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: 2 }}>
        {species.map((s, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: same species can appear twice in a 6-mon team
          <Sprite key={`${s}-${i}`} species={s} size={24} />
        ))}
      </div>
    </div>
  );
}
